<script lang="ts">
	import type { PageProps } from "./$types";
	import { Button } from "#lib/web-ui/ui/button/index.js";
	import ContactForm from "./ContactForm.svelte";
	import UsersRoundIcon from "@lucide/svelte/icons/users-round";
	import CalculatorIcon from "@lucide/svelte/icons/calculator";
	import PhoneIcon from "@lucide/svelte/icons/phone";
	import MailIcon from "@lucide/svelte/icons/mail";
	import CircleDollarSignIcon from "@lucide/svelte/icons/circle-dollar-sign";
	import MapPinIcon from "@lucide/svelte/icons/map-pin";
	import BuildingIcon from "@lucide/svelte/icons/building";
	import MegaphoneIcon from "@lucide/svelte/icons/megaphone";
	import HandshakeIcon from "@lucide/svelte/icons/handshake";
	import Building2Icon from "@lucide/svelte/icons/building-2";

	let { data }: PageProps = $props();
	const teams = [
		{
			name: "Marketing",
			icon: MegaphoneIcon,
			email: "marketing@fergmar.com"
		},
		{
			name: "Sales",
			icon: HandshakeIcon,
			email: "sales@fergmar.com"
		},
		{
			name: "Acquisitions",
			icon: Building2Icon,
			email: "acquisitions@fergmar.com"
		},
		{
			name: "Property Management",
			icon: BuildingIcon,
			email: "leasing@fergmar.com"
		},
		{
			name: "Human Resources",
			icon: UsersRoundIcon,
			email: "hr@fergmar.com"
		},
		{
			name: "Accounting",
			icon: CalculatorIcon,
			email: "accounting@fergmar.com"
		},
		{
			name: "Investor Relations",
			icon: CircleDollarSignIcon,
			email: "invest@fergmar.com"
		}
	];
</script>

<svelte:head>
	<title>Contact Fergmar | Real Estate in Victoria, TX</title>
	<meta
		name="description"
		content="Have questions or ready to get started? Contact Fergmar today to speak with our team about your real estate goals."
	/>
	<meta
		name="keywords"
		content="real estate investment, property syndication, passive income, multi-family investments, commercial real estate, investment management, wealth creation, real estate portfolio, value-add investments"
	/>
	<meta name="viewport" content="width=device-width, initial-scale=1.0" />
	<meta property="og:title" content="Contact Fergmar | Real Estate in Victoria, TX" />
	<meta property="og:site_name" content="Fergmar" />
	<meta
		property="og:description"
		content="Have questions or ready to get started? Contact Fergmar today to speak with our team about your real estate goals."
	/>
</svelte:head>

<div class="w-full">
	<!-- Hero section with pattern background -->
	<div class="from-primary/5 to-primary/10 border-b bg-linear-to-r">
		<div class="relative container mx-auto max-w-7xl px-4 py-8 sm:px-6">
			<!-- Background pattern -->
			<div class="absolute inset-0 overflow-hidden opacity-10">
				<div
					class="bg-primary absolute top-0 left-1/3 h-96 w-96 rounded-full mix-blend-multiply blur-3xl filter"
				></div>
				<div
					class="bg-primary/80 absolute right-1/3 bottom-0 h-96 w-96 rounded-full mix-blend-multiply blur-3xl filter"
				></div>
			</div>

			<div class="relative">
				<h1 class="text-primary font-semibold">Contact Us</h1>
				<h2 class="mt-2 text-3xl font-semibold md:text-4xl">We'd love to hear from you</h2>
				<p class="text-foreground/70 mt-3 max-w-3xl">
					Whether you're looking to discuss strategic acquisitions, asset management
					solutions, property operations, or development opportunities, our dedicated team
					is here to help. We're committed to delivering exceptional real estate solutions
					that create lasting value for our partners and positive impact in communities.
				</p>
			</div>
		</div>
	</div>

	<div class="container mx-auto max-w-7xl px-4 py-6 sm:px-6">
		<!-- Main content grid with sidebar -->
		<div class="grid grid-cols-1 gap-6 lg:grid-cols-3">
			<!-- Main column - Contact form -->
			<div class="order-1 lg:order-1 lg:col-span-2">
				<h2 class="mb-4 text-xl font-semibold">Send us a message</h2>
				<ContactForm contactForm={data.form} />
			</div>

			<!-- Sidebar - Contact Info & Department Contacts -->
			<div class="order-2 space-y-6 lg:order-2 lg:col-span-1">
				<!-- Contact Information Section -->
				<div class="rounded border bg-gray-50 p-4 shadow-sm">
					<h2 class="mb-4 text-xl font-semibold">Contact Information</h2>

					<div class="space-y-4">
						<div class="space-y-1">
							<h3 class="text-base font-semibold">Call us</h3>
							<p class="text-muted-foreground text-xs">
								Call our team Mon-Fri from 8am to 5pm.
							</p>
							<a href="tel:+13615824663">
								<Button
									variant="outline"
									class="mt-2 flex h-auto w-full items-center justify-start gap-2 py-2 transition-all duration-300 hover:scale-105 hover:shadow-md"
								>
									<PhoneIcon class="h-4 w-4" />
									<span class="font-medium">+1 (361) 582-4663</span>
								</Button>
							</a>
						</div>

						<div class="space-y-1">
							<h3 class="text-base font-semibold">Email us</h3>
							<p class="text-muted-foreground text-xs">
								We'll respond within 24-48 hours.
							</p>
							<a href="mailto:info@fergmar.com">
								<Button
									variant="outline"
									class="mt-2 flex h-auto w-full items-center justify-start gap-2 py-2 transition-all duration-300 hover:scale-105 hover:shadow-md"
								>
									<MailIcon class="h-4 w-4" />
									Send us an email
								</Button>
							</a>
						</div>

						<div class="space-y-1">
							<h3 class="text-base font-semibold">Visit us</h3>
							<p class="text-muted-foreground text-xs">Visit our office in person.</p>
							<a
								target="_blank"
								href="https://www.google.com/maps?sca_esv=e74a10221791e7e3&biw=1385&bih=2402&output=search&q=Fergmar&source=lnms&fbs=ABzOT_CWdhQLP1FcmU5B0fn3xuWpA-dk4wpBWOGsoR7DG5zJBv10Kbgy3ptSBM6mMfaz8zDVX4b2W1tiDkb3uUgOX2bJkgS24a4hEMkFu9wUyGFHJMOuKVUiSlASRG8DIeguSCGlrjXikgXnuRhXn5nr9VGq1g5zkyQD6un2r4KKBh7KOjCzBegtHJM4RYpW5V_vlPIP8eED_Z7LGWeglCrVwkFP5fnZtA&entry=mc&ved=1t:200715&ictx=111"
							>
								<Button
									variant="outline"
									class="mt-2 flex h-auto w-full items-center justify-start gap-2 py-2 transition-all duration-300 hover:scale-105 hover:shadow-md"
								>
									<MapPinIcon class="h-4 w-4" />
									<div class="flex flex-col items-start">
										<p class="text-sm font-medium">
											4800 N Navarro St, Ste 100
										</p>
										<p class="text-muted-foreground text-xs">
											Victoria, TX 77901
										</p>
									</div>
								</Button>
							</a>
						</div>
					</div>
				</div>

				<!-- Department Contacts Section -->
				<div class="order-3 rounded border bg-gray-50 p-4 shadow-sm">
					<h2 class="mb-4 text-xl font-semibold">Team Contacts</h2>

					<div class="space-y-3">
						{#each teams as team}
							<a
								href={`mailto:${team.email}`}
								class="flex items-center border-b pb-3 transition-all duration-300 last:border-b-0 last:pb-0 hover:scale-105 hover:shadow-md"
							>
								<div
									class="bg-primary mr-3 flex h-8 w-8 shrink-0 items-center justify-center rounded"
								>
									<team.icon class="text-background h-4 w-4" />
								</div>
								<div class="flex-1">
									<p class="text-sm font-medium">{team.name}</p>
									<span
										class="text-foreground/80 hover:text-primary text-xs transition-colors"
									>
										{team.email}
									</span>
								</div>
							</a>
						{/each}
					</div>
				</div>
			</div>
		</div>
	</div>
</div>
